/**
 * Sales Team Performance Report Renderer
 *
 * Hierarchical display of sales managers with their salespeople.
 * Each manager section is expandable, showing a table of salespeople
 * with units, front gross, holdback, F&I, and total gross.
 * Blue theme matching NEW_VEHICLE department.
 */

import React, { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

interface SalespersonMetrics {
  salespersonId: string;
  salespersonName: string;
  totalUnits: number;
  newUnits: number;
  usedUnits: number;
  swapUnits: number;
  wholesaleUnits: number;
  frontGross: number;
  fiGross: number;
  holdback: number;
  totalGross: number;
  avgFrontGrossPerUnit: number | null;
  avgFiGrossPerUnit: number | null;
  avgHoldbackPerUnit: number | null;
  avgTotalGrossPerUnit: number | null;
}

interface ManagerMetrics {
  managerId: string;
  managerName: string;
  totalUnits: number;
  newUnits: number;
  usedUnits: number;
  swapUnits: number;
  wholesaleUnits: number;
  frontGross: number;
  fiGross: number;
  holdback: number;
  totalGross: number;
  avgFrontGrossPerUnit: number | null;
  avgFiGrossPerUnit: number | null;
  avgHoldbackPerUnit: number | null;
  avgTotalGrossPerUnit: number | null;
  salespeople: SalespersonMetrics[];
}

export interface SalesTeamPerformanceData {
  managers: ManagerMetrics[];
  summary: {
    managerCount: number;
    salespersonCount: number;
    totalUnits: number;
    newUnits: number;
    usedUnits: number;
    swapUnits: number;
    wholesaleUnits: number;
    frontGross: number;
    fiGross: number;
    holdback: number;
    totalGross: number;
    avgFrontGrossPerUnit: number | null;
    avgFiGrossPerUnit: number | null;
    avgHoldbackPerUnit: number | null;
    avgTotalGrossPerUnit: number | null;
  };
  lastUpdated: string | null;
}

const fmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

function formatCurrency(value: number): string {
  if (value < 0) {
    return `(${fmt.format(Math.abs(value))})`;
  }
  return fmt.format(value);
}

function formatAvg(value: number | null): string {
  if (value === null) return '--';
  const f = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  if (value < 0) return `(${f.format(Math.abs(value))})`;
  return f.format(value);
}

function colorClass(value: number): string {
  if (value < 0) return 'text-red-600';
  return 'text-gray-900';
}

interface Props {
  data: SalesTeamPerformanceData;
}

export const SalesTeamPerformanceReport: React.FC<Props> = ({ data }) => {
  const { managers, summary } = data;
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(managers.map((m) => m.managerId)));

  const toggleManager = (managerId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(managerId)) {
        next.delete(managerId);
      } else {
        next.add(managerId);
      }
      return next;
    });
  };

  const expandAll = () => setExpanded(new Set(managers.map((m) => m.managerId)));
  const collapseAll = () => setExpanded(new Set());

  return (
    <div className="sales-team-performance-report">
      <style>{`
        @media print {
          @page { size: landscape; margin: 0.4in; }
          .sales-team-performance-report { width: 100% !important; overflow: visible !important; }
          .sales-team-performance-report table { width: 100% !important; font-size: 8pt !important; }
          .sales-team-performance-report td,
          .sales-team-performance-report th { padding: 2px 4px !important; }
          .no-print { display: none !important; }
          .print-expand { display: table-row !important; }
        }
      `}</style>

      {/* Summary Bar */}
      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
          <span className="font-semibold text-blue-900">
            Total Gross: <span className={colorClass(summary.totalGross)}>{formatCurrency(summary.totalGross)}</span>
          </span>
          <span className="text-gray-700">
            Units: {summary.totalUnits} ({summary.newUnits}N / {summary.usedUnits}U)
          </span>
          {(summary.swapUnits > 0 || summary.wholesaleUnits > 0) && (
            <span className="text-gray-500 text-xs">
              {summary.swapUnits > 0 && `${summary.swapUnits} Swap`}
              {summary.swapUnits > 0 && summary.wholesaleUnits > 0 && ' / '}
              {summary.wholesaleUnits > 0 && `${summary.wholesaleUnits} Wholesale`}
            </span>
          )}
          <span className="text-gray-500">{summary.managerCount} Managers</span>
          <span className="text-gray-500">{summary.salespersonCount} Salespeople</span>
        </div>
      </div>

      {/* Expand/Collapse controls */}
      <div className="flex gap-2 mb-3 no-print">
        <button
          onClick={expandAll}
          className="text-xs text-blue-600 hover:text-blue-800 underline"
        >
          Expand All
        </button>
        <span className="text-gray-300">|</span>
        <button
          onClick={collapseAll}
          className="text-xs text-blue-600 hover:text-blue-800 underline"
        >
          Collapse All
        </button>
      </div>

      {/* Manager sections */}
      {managers.map((mgr) => {
        const isExpanded = expanded.has(mgr.managerId);

        return (
          <div key={mgr.managerId} className="mb-4 border border-gray-200 rounded-lg overflow-hidden">
            {/* Manager header */}
            <button
              onClick={() => toggleManager(mgr.managerId)}
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors no-print"
            >
              <div className="flex items-center gap-2">
                {isExpanded ? (
                  <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                )}
                <span className="font-semibold text-gray-900 uppercase">
                  {mgr.managerName}
                </span>
                <span className="text-xs text-gray-500">(Sales Manager)</span>
              </div>
              <span className={`font-semibold font-mono ${colorClass(mgr.totalGross)}`}>
                {formatCurrency(mgr.totalGross)}
              </span>
            </button>

            {/* Print-only header (always visible) */}
            <div className="hidden print:block px-4 py-2 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <span className="font-bold text-gray-900 uppercase">{mgr.managerName} (Sales Manager)</span>
                <span className={`font-semibold font-mono ${colorClass(mgr.totalGross)}`}>
                  {formatCurrency(mgr.totalGross)}
                </span>
              </div>
            </div>

            {/* Manager summary line */}
            <div className={`px-4 py-2 bg-white border-t border-gray-100 text-xs text-gray-600 ${isExpanded ? '' : 'hidden'} print:block`}>
              Front: {formatCurrency(mgr.frontGross)} | Holdback: {formatCurrency(mgr.holdback)} | F&I: {formatCurrency(mgr.fiGross)} | Units: {mgr.totalUnits} ({mgr.newUnits}N / {mgr.usedUnits}U)
              {(mgr.swapUnits > 0 || mgr.wholesaleUnits > 0) && (
                <span className="text-gray-400 ml-1">
                  {mgr.swapUnits > 0 && `+ ${mgr.swapUnits} Swap`}
                  {mgr.wholesaleUnits > 0 && ` + ${mgr.wholesaleUnits} WS`}
                </span>
              )}
            </div>

            {/* Salesperson table */}
            <div className={`${isExpanded ? '' : 'hidden'} print:block`}>
              {mgr.salespeople.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-400 italic">
                  No salespeople on record for this manager
                </div>
              ) : (
                <div className="overflow-x-auto print:overflow-visible">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50/50">
                        <th className="text-left py-1.5 px-3 font-medium text-gray-600 text-xs">Salesperson</th>
                        <th className="text-right py-1.5 px-3 font-medium text-gray-600 text-xs">Units</th>
                        <th className="text-right py-1.5 px-3 font-medium text-gray-600 text-xs">N / U</th>
                        <th className="text-right py-1.5 px-3 font-medium text-gray-600 text-xs">Front Gross</th>
                        <th className="text-right py-1.5 px-3 font-medium text-gray-600 text-xs">Holdback</th>
                        <th className="text-right py-1.5 px-3 font-medium text-gray-600 text-xs">F&I</th>
                        <th className="text-right py-1.5 px-3 font-medium text-gray-600 text-xs">Total Gross</th>
                        <th className="text-right py-1.5 px-3 font-medium text-gray-600 text-xs">Avg / Unit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mgr.salespeople.map((sp) => (
                        <tr key={sp.salespersonId} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-1.5 px-3 text-gray-900">{sp.salespersonName}</td>
                          <td className="py-1.5 px-3 text-right font-mono text-gray-700">{sp.totalUnits}</td>
                          <td className="py-1.5 px-3 text-right font-mono text-gray-500 text-xs">
                            {sp.newUnits}N / {sp.usedUnits}U
                            {(sp.swapUnits > 0 || sp.wholesaleUnits > 0) && (
                              <span className="text-gray-400 block text-[10px]">
                                {sp.swapUnits > 0 && `${sp.swapUnits}S`}
                                {sp.swapUnits > 0 && sp.wholesaleUnits > 0 && '/'}
                                {sp.wholesaleUnits > 0 && `${sp.wholesaleUnits}W`}
                              </span>
                            )}
                          </td>
                          <td className={`py-1.5 px-3 text-right font-mono ${colorClass(sp.frontGross)}`}>
                            {formatCurrency(sp.frontGross)}
                          </td>
                          <td className={`py-1.5 px-3 text-right font-mono ${colorClass(sp.holdback)}`}>
                            {formatCurrency(sp.holdback)}
                          </td>
                          <td className={`py-1.5 px-3 text-right font-mono ${colorClass(sp.fiGross)}`}>
                            {formatCurrency(sp.fiGross)}
                          </td>
                          <td className={`py-1.5 px-3 text-right font-mono font-medium ${colorClass(sp.totalGross)}`}>
                            {formatCurrency(sp.totalGross)}
                          </td>
                          <td className="py-1.5 px-3 text-right font-mono text-gray-500 text-xs">
                            {formatAvg(sp.avgTotalGrossPerUnit)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {/* Manager subtotal */}
                    <tfoot>
                      <tr className="border-t-2 border-gray-200 bg-gray-50 font-semibold text-xs">
                        <td className="py-1.5 px-3 text-gray-700">Manager Total</td>
                        <td className="py-1.5 px-3 text-right font-mono">{mgr.totalUnits}</td>
                        <td className="py-1.5 px-3 text-right font-mono text-gray-500">
                          {mgr.newUnits}N / {mgr.usedUnits}U
                        </td>
                        <td className={`py-1.5 px-3 text-right font-mono ${colorClass(mgr.frontGross)}`}>
                          {formatCurrency(mgr.frontGross)}
                        </td>
                        <td className={`py-1.5 px-3 text-right font-mono ${colorClass(mgr.holdback)}`}>
                          {formatCurrency(mgr.holdback)}
                        </td>
                        <td className={`py-1.5 px-3 text-right font-mono ${colorClass(mgr.fiGross)}`}>
                          {formatCurrency(mgr.fiGross)}
                        </td>
                        <td className={`py-1.5 px-3 text-right font-mono ${colorClass(mgr.totalGross)}`}>
                          {formatCurrency(mgr.totalGross)}
                        </td>
                        <td className="py-1.5 px-3 text-right font-mono text-gray-500">
                          {formatAvg(mgr.avgTotalGrossPerUnit)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Grand Total */}
      <div className="mt-6 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
        <h3 className="text-sm font-bold text-blue-900 mb-2 uppercase">Grand Total</h3>
        <div className="overflow-x-auto print:overflow-visible">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-blue-200">
                <th className="text-right py-1 px-3 font-medium text-blue-700 text-xs">Units</th>
                <th className="text-right py-1 px-3 font-medium text-blue-700 text-xs">N / U</th>
                <th className="text-right py-1 px-3 font-medium text-blue-700 text-xs">Front Gross</th>
                <th className="text-right py-1 px-3 font-medium text-blue-700 text-xs">Holdback</th>
                <th className="text-right py-1 px-3 font-medium text-blue-700 text-xs">F&I</th>
                <th className="text-right py-1 px-3 font-medium text-blue-700 text-xs">Total Gross</th>
                <th className="text-right py-1 px-3 font-medium text-blue-700 text-xs">Avg / Unit</th>
              </tr>
            </thead>
            <tbody>
              <tr className="font-bold">
                <td className="py-2 px-3 text-right font-mono text-blue-900">{summary.totalUnits}</td>
                <td className="py-2 px-3 text-right font-mono text-blue-700 text-xs">
                  {summary.newUnits}N / {summary.usedUnits}U
                  {(summary.swapUnits > 0 || summary.wholesaleUnits > 0) && (
                    <span className="text-blue-400 block text-[10px]">
                      {summary.swapUnits > 0 && `${summary.swapUnits}S`}
                      {summary.swapUnits > 0 && summary.wholesaleUnits > 0 && '/'}
                      {summary.wholesaleUnits > 0 && `${summary.wholesaleUnits}W`}
                    </span>
                  )}
                </td>
                <td className={`py-2 px-3 text-right font-mono ${summary.frontGross < 0 ? 'text-red-600' : 'text-blue-900'}`}>
                  {formatCurrency(summary.frontGross)}
                </td>
                <td className={`py-2 px-3 text-right font-mono ${summary.holdback < 0 ? 'text-red-600' : 'text-blue-900'}`}>
                  {formatCurrency(summary.holdback)}
                </td>
                <td className={`py-2 px-3 text-right font-mono ${summary.fiGross < 0 ? 'text-red-600' : 'text-blue-900'}`}>
                  {formatCurrency(summary.fiGross)}
                </td>
                <td className={`py-2 px-3 text-right font-mono ${summary.totalGross < 0 ? 'text-red-600' : 'text-blue-900'}`}>
                  {formatCurrency(summary.totalGross)}
                </td>
                <td className="py-2 px-3 text-right font-mono text-blue-600 text-xs">
                  {formatAvg(summary.avgTotalGrossPerUnit)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {managers.length === 0 && (
        <div className="text-center py-8 text-gray-400">
          No sales team data found for the selected period.
        </div>
      )}

      <div className="px-1 py-2 text-xs text-gray-500">
        2-salesperson deals credit each rep ½ unit and half the gross, so unit counts can end
        in .5; team and location totals are unchanged.
      </div>
    </div>
  );
};
